import { gql, useLazyQuery, useQuery } from '@apollo/client'
import { flags, useFeatureFlags } from '../../../../lib/feature-flags'
import moment from 'moment'
import React from 'react'
import styled from 'styled-components'
import { COACH_TOOLS_API } from '../../../../apolloClient'
import { white } from '../../../../assets/colors'
import { Button } from '../../../Button'
import { pgaComTld } from '../../../utils'
import { formatDate } from '../../../utils/formatter'
import { CardBox, DynamicInfoFrame, InfoLabel } from '../../shared'
import ErrorLoadingDues from './ErrorLoadingDues'
import LoadingDues from './LoadingDues'
import { ActionLink, ActionsContainer, Layout, LeftHalf, LinkIcon, RightHalf } from './styled'

export const OrderStage = {
  DRAFT: 'draft',
  OPEN: 'open',
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed'
}

export const CARD_HEIGHT = '190px'

const PAY_DUES_URL = `https://${pgaComTld}/member/dues`
const INVOICE_LIST_URL = `https://${pgaComTld}/member/account/invoices`
const PAY_DUES_PROXY_URL = `https://${pgaComTld}/member/dues?assignProxy=true`

export const formatCents = (value, minimumFractionDigits = 2) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits }).format((value || 0) / 100)

const CardBoxPadding = styled(CardBox)`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  margin: 0;
  min-height: 190px;
  padding: 1rem 1.5rem;
  @media (min-width: 768px) {
    flex-basis: 48%;
  }

  @media (min-width: 992px) {
    flex-basis: 33%;
  }

  @media(max-width: 767.98px) {
    flex: 1;
    min-height: 218px;
  }
`

export const DUES_QUERY = gql`
  query DuesQuery {
    duesCycle: currentDuesCycle {
      id
      dueDate
      endsOn
      order {
        id
        balance
        dueDate
        stage
        payable
        pdfAvailable
        lineItems {
          id
          name
        }
      }
    }
  }
`

export const PDF_QUERY = gql`
  query PDFQuery {
    duesCycle: currentDuesCycle {
      id
      order {
        id
        pdfUrl
      }
    }
  }
`

function getNextCycleDate (endsOn) {
  if (endsOn) {
    return moment(endsOn).add(1, 'day').format('MM/YYYY')
  }
}

const DownloadInvoice = ({ orderStage }) => {
  const [pdfQuery, { loading }] = useLazyQuery(PDF_QUERY, {
    context: {
      api: COACH_TOOLS_API
    }
  })

  const handleOrderPDFClick = async () => {
    const { data } = await pdfQuery()
    const pdfUrl = data?.duesCycle?.order?.pdfUrl
    pdfUrl && (window.location.assign(pdfUrl))
  }

  return (
    <>
      <ActionLink onClick={handleOrderPDFClick}>
        Print {orderStage === OrderStage.PAID ? 'Receipt' : 'Invoice'} <LinkIcon />
      </ActionLink>
      {loading && <p className='mt-1'>Loading...</p>}
    </>
  )
}

export const getTotalBalance = (orders) => {
  if (!orders || !orders.length) return 0

  const cutoffDate = new Date('2023-12-31T23:59:59Z')

  return orders.reduce((total, order) => {
    if (!order.payable) return total

    const orderDate = new Date(order.createdAt)
    if (orderDate <= cutoffDate) return total

    return total + (order.balance || 0)
  }, 0)
}

const OtherOutstandingInvoices = ({ coachToolsOrders }) => {
  const coachToolsBalance = getTotalBalance(coachToolsOrders)

  return (
    <>
      <InfoLabel fs='12px' className='text-uppercase' data-testid='dues-title'>
        OUTSTANDING INVOICES
      </InfoLabel>
      <Layout className='mt-4 mt-md-3'>
        <LeftHalf>
          <InfoLabel className='text-uppercase'>
            Status
          </InfoLabel>
          <DynamicInfoFrame fs='16px' noPadX className='text-warning' data-testid='dues-status'>
            Payment Required
          </DynamicInfoFrame>
        </LeftHalf>
        <RightHalf>
          <InfoLabel className='text-uppercase'>
            Balance
          </InfoLabel>
          <DynamicInfoFrame noPadX data-testid='dues-balance'>
            {formatCents(coachToolsBalance)}
          </DynamicInfoFrame>
        </RightHalf>
      </Layout>
      <Layout align='flex-end' className='mt-3 mt-md-2'>
        <LeftHalf>
          <ActionsContainer />
        </LeftHalf>
        <RightHalf>
          <a href={`${INVOICE_LIST_URL}?slug=invoices`}>
            <Button margin='0px' data-testid='pay-dues-button'>
              VIEW INVOICES
            </Button>
          </a>
        </RightHalf>
      </Layout>
    </>
  )
}

export const MembershipDues = ({ data, coachToolsOrders }) => {
  const [isNewPaymentFlowEnabled] = useFeatureFlags([flags.FLAG_FEAT_MEMBERSHIP_NEW_PAYMENT_FLOW])

  const duesCycle = data.duesCycle
  const order = duesCycle.order
  const hasBalance = (order?.balance || 0) > 0
  const coachToolsBalance = getTotalBalance(coachToolsOrders)
  const duesOrders = coachToolsOrders?.filter(order => order.orderType === 'dues') || []
  const duesBalance = getTotalBalance(duesOrders)

  const showCoachTools = !hasBalance && coachToolsBalance > 0 && duesOrders.length === 1

  if (showCoachTools) {
    return <OtherOutstandingInvoices coachToolsOrders={coachToolsOrders} />
  }

  return (
    <>
      <InfoLabel fs='12px' className='text-uppercase' data-testid='dues-title'>
        DUES | {hasBalance ? 'Billing Cycle Open' : `Next Cycle ${getNextCycleDate(duesCycle?.endsOn)}`}
      </InfoLabel>
      <Layout className='mt-4 mt-md-3'>
        <LeftHalf>
          <InfoLabel className='text-uppercase'>
            {hasBalance ? 'Due Date' : 'Status'}
          </InfoLabel>
          <DynamicInfoFrame noPadX className={hasBalance && order?.dueDate ? '' : 'text-success'} data-testid='dues-status'>
            {hasBalance && order?.dueDate ? formatDate(order.dueDate) : 'Current'}
          </DynamicInfoFrame>
        </LeftHalf>
        <RightHalf>
          <InfoLabel className='text-uppercase'>
            Balance
          </InfoLabel>
          <DynamicInfoFrame noPadX data-testid='dues-balance'>
            {duesOrders.length > 1 && isNewPaymentFlowEnabled ? formatCents(duesBalance) : formatCents(order?.balance || 0)}
          </DynamicInfoFrame>
        </RightHalf>
      </Layout>
      <Layout align='flex-end' className='mt-3 mt-md-2'>
        {duesOrders.length > 1 && isNewPaymentFlowEnabled
          ? (
            <>
              <LeftHalf />
              <RightHalf>
                {hasBalance
                  ? (
                    <a href={`${INVOICE_LIST_URL}?slug=invoices`}>
                      <Button margin='0px' data-testid='view-invoices-button'>
                        View Invoices
                      </Button>
                    </a>
                    )
                  : (
                    <a href={`${INVOICE_LIST_URL}?slug=receipts`}>
                      <Button margin='0px'>
                        View Receipts
                      </Button>
                    </a>)}
              </RightHalf>
            </>
            )
          : (
            <>
              <LeftHalf>
                <ActionsContainer>
                  {hasBalance && order?.payable && (
                    <a href={PAY_DUES_PROXY_URL} target='_blank' className='mb-1' rel='noopener noreferrer'>
                      Assign Proxy <LinkIcon />
                    </a>
                  )}
                  {order?.pdfAvailable && (
                    <DownloadInvoice orderStage={order?.stage} />
                  )}
                </ActionsContainer>
              </LeftHalf>
              <RightHalf>
                {hasBalance
                  ? (
                    <a href={PAY_DUES_URL}>
                      <Button margin='0px' data-testid='pay-dues-button' disabled={!order?.payable}>
                        PAY DUES
                      </Button>
                    </a>
                    )
                  : (
                    <a href={`${INVOICE_LIST_URL}?slug=receipts`}>
                      <Button margin='0px'>
                        View Receipts
                      </Button>
                    </a>)}
              </RightHalf>
            </>
            )}
      </Layout>
    </>
  )
}

export default function MembershipCommerceDues ({ coachToolsOrders }) {
  const { data, error, loading, refetch } = useQuery(DUES_QUERY, {
    fetchPolicy: 'cache-and-network',
    context: { api: COACH_TOOLS_API }
  })

  if (loading) {
    return <LoadingDues />
  } else if (error) {
    return <ErrorLoadingDues refetch={refetch} />
  }
  return (
    <CardBoxPadding color={white}>
      <MembershipDues data={data} coachToolsOrders={coachToolsOrders} />
    </CardBoxPadding>
  )
}
